import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { SatisfactionDueView } from '../api/reads.js'

/**
 * `OPS-207` (SHOULD): "Periodic satisfaction measurement."
 *
 * **One banner above the work queue, and it goes away.** `SCR-01` makes the work queue the landing
 * screen and it must not turn into a dashboard, so this is a single dismissible row that renders
 * only when the server says the owner is due, and nothing at all otherwise.
 *
 * **Never blocking, and the first version of this said so while being able to stop a shop
 * trading.** A failed read renders nothing, like `Onboarding`: a question about Lensmira is not
 * worth an error banner over the screen a shop works from. "Not now" is as good an answer as a
 * score, because the server restarts the interval for either, so a shop is not nagged.
 *
 * What made the claim false was on the server. An answer arriving after the interval had restarted
 * was refused with a 409, a 409 marks a queued write `blocked`, and a blocked head stops the queue,
 * so every sale and payment behind it waited on a satisfaction question. The owner answering on
 * their phone and then somebody clearing the stale banner at the counter was enough to do it. The
 * server now answers `outcome: 'redundant'` with a 200 and writes nothing, which is what "never
 * blocking" has to mean when the write goes through a queue that halts on refusals.
 *
 * **Only the owner is shown it.** The server enforces that (`subscription_billing`), and `App`
 * does not mount this for anybody else, so no other role ever makes the request and gets a 403.
 *
 * **Both answers go through the write queue** (`OFF-001a`). The queue mints the idempotency key
 * once at enqueue, which is what makes a retried answer one row.
 */

export interface SatisfactionProps {
  readonly load: () => Promise<SatisfactionDueView>
}

type Phase = 'asking' | 'thanked' | 'gone'

export function Satisfaction({ load }: SatisfactionProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue } = useWriteQueue()
  const [view, setView] = useState<SatisfactionDueView | null>(null)
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [phase, setPhase] = useState<Phase>('asking')

  useEffect(() => {
    let live = true
    void load()
      .then((loaded) => {
        if (live) setView(loaded)
      })
      .catch(() => {
        // Silent on purpose: see the header. `view` stays null and nothing renders.
      })
    return () => {
      live = false
    }
  }, [load])

  const send = useCallback(async () => {
    if (score === null) return
    const trimmed = comment.trim()
    setPhase('thanked')
    await enqueue({
      method: 'POST',
      path: '/satisfaction',
      label: t('satisfaction.queueLabelAnswer'),
      body: trimmed === '' ? { score } : { score, comment: trimmed },
    })
  }, [comment, enqueue, score, t])

  const dismiss = useCallback(async () => {
    setPhase('gone')
    await enqueue({
      method: 'POST',
      path: '/satisfaction',
      label: t('satisfaction.queueLabelDismiss'),
      body: { dismissed: true },
    })
  }, [enqueue, t])

  if (view === null || !view.due || phase === 'gone') return null

  if (phase === 'thanked') {
    return (
      <section className="lm-satisfaction" aria-live="polite">
        <p className="lm-satisfaction__thanks">{t('satisfaction.thanks')}</p>
      </section>
    )
  }

  const points: number[] = []
  for (let n = view.min; n <= view.max; n += 1) points.push(n)

  return (
    <section className="lm-satisfaction" aria-labelledby="satisfaction-heading">
      <h2 id="satisfaction-heading" className="lm-satisfaction__heading">
        {t('satisfaction.heading')}
      </h2>
      <fieldset className="lm-satisfaction__scale">
        <legend className="lm-hint">{t('satisfaction.question')}</legend>
        <div className="lm-satisfaction__points">
          {points.map((point) => (
            <button
              key={point}
              type="button"
              className={
                score === point
                  ? 'lm-button lm-button--primary lm-satisfaction__point'
                  : 'lm-button lm-button--secondary lm-satisfaction__point'
              }
              aria-pressed={score === point}
              onClick={() => {
                setScore(point)
              }}
            >
              {String(point)}
            </button>
          ))}
        </div>
        <p className="lm-hint lm-satisfaction__ends">
          <span>{t('satisfaction.scaleLow')}</span>
          <span>{t('satisfaction.scaleHigh')}</span>
        </p>
      </fieldset>

      <label className="lm-field">
        <span className="lm-field__label">{t('satisfaction.commentLabel')}</span>
        <textarea
          className="lm-input lm-input--area"
          dir="auto"
          maxLength={view.commentMaxLength}
          value={comment}
          onChange={(event) => {
            setComment(event.target.value)
          }}
        />
      </label>
      {/* The CUS-008 warning, stated where the free text is typed. The text goes to Lensmira and
          not into the shop's records, so a customer's name in it would leave the tenant. */}
      <p className="lm-hint">{t('satisfaction.commentWarning')}</p>

      <div className="lm-satisfaction__actions">
        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={score === null}
          onClick={() => {
            void send()
          }}
        >
          {t('satisfaction.send')}
        </button>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            void dismiss()
          }}
        >
          {t('satisfaction.dismiss')}
        </button>
      </div>
    </section>
  )
}
